import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../../lib/api';
import { formatDate, formatDateTime, cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Calendar,
  FileText,
  CheckSquare,
  MessageSquare,
  DollarSign,
  Flag,
  ChevronDown,
  ChevronUp,
  Loader2,
  ExternalLink,
  Filter,
  X,
  Gavel,
  AlertTriangle,
  Clock,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const TIMELINE_ICONS = {
  event: Calendar,
  document: FileText,
  task: CheckSquare,
  note: MessageSquare,
  invoice: DollarSign,
  status_change: Flag,
  court_appearance: Gavel,
  filing_deadline: AlertTriangle,
  limitation_deadline: AlertTriangle,
};

const TIMELINE_COLORS = {
  event: 'bg-blue-100 text-blue-700 border-blue-200',
  document: 'bg-green-100 text-green-700 border-green-200',
  task: 'bg-purple-100 text-purple-700 border-purple-200',
  note: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  invoice: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  status_change: 'bg-orange-100 text-orange-700 border-orange-200',
  court_appearance: 'bg-purple-200 text-purple-800 border-purple-300',
  filing_deadline: 'bg-red-100 text-red-700 border-red-200',
  limitation_deadline: 'bg-red-200 text-red-800 border-red-300',
};

const STATUS_COLORS = {
  completed: 'bg-green-100 text-green-700',
  scheduled: 'bg-blue-100 text-blue-700',
  pending: 'bg-yellow-100 text-yellow-700',
  overdue: 'bg-red-100 text-red-700',
  signed: 'bg-green-100 text-green-700',
  uploaded: 'bg-slate-100 text-slate-700',
  draft: 'bg-slate-100 text-slate-700',
  paid: 'bg-green-100 text-green-700',
  sent: 'bg-blue-100 text-blue-700',
};

export const CaseTimeline = ({ caseId, onItemClick }) => {
  const [timeline, setTimeline] = useState([]);
  const [caseInfo, setCaseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    if (caseId) {
      loadTimeline();
    }
  }, [caseId]);

  const loadTimeline = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.getCaseTimeline(caseId);
      setTimeline(response.data.timeline || []);
      setCaseInfo({
        title: response.data.case_title,
        status: response.data.case_status,
        stage: response.data.case_stage,
        totalItems: response.data.total_items,
      });
    } catch (err) {
      console.error('Failed to load timeline:', err);
      setError(err.response?.data?.detail || 'Failed to load timeline');
      if (err.response?.status === 403) {
        setError('You do not have access to this case timeline');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredTimeline = filterType === 'all' 
    ? timeline 
    : timeline.filter(item => item.type === filterType);

  // Group by date
  const groupedTimeline = filteredTimeline.reduce((groups, item) => {
    const date = item.date ? new Date(item.date).toLocaleDateString() : 'Unknown Date';
    if (!groups[date]) groups[date] = [];
    groups[date].push(item);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-6 text-center">
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <p className="text-red-700">{error}</p>
          <Button variant="outline" className="mt-4" onClick={loadTimeline}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg text-slate-900">Case Timeline</h3>
          {caseInfo && (
            <p className="text-sm text-slate-500">
              {caseInfo.totalItems} events • {caseInfo.title}
            </p>
          )}
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[150px]">
            <Filter className="h-4 w-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items</SelectItem>
            <SelectItem value="event">Events</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="task">Tasks</SelectItem>
            <SelectItem value="note">Notes</SelectItem>
            <SelectItem value="invoice">Invoices</SelectItem>
            <SelectItem value="status_change">Milestones</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Timeline */}
      <ScrollArea className="h-[600px] pr-4">
        {Object.keys(groupedTimeline).length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p>No timeline events found</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />

            {Object.entries(groupedTimeline).map(([date, items]) => (
              <div key={date} className="mb-6">
                {/* Date header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center z-10">
                    <Clock className="h-4 w-4 text-slate-600" />
                  </div>
                  <span className="font-medium text-sm text-slate-600">{date}</span>
                </div>

                {/* Items for this date */}
                <div className="ml-12 space-y-3">
                  {items.map((item) => {
                    const IconComponent = TIMELINE_ICONS[item.subtype] || TIMELINE_ICONS[item.type] || Calendar;
                    const colorClass = TIMELINE_COLORS[item.subtype] || TIMELINE_COLORS[item.type] || 'bg-slate-100 text-slate-700 border-slate-200';
                    const statusClass = STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-700';
                    const isExpanded = expandedItems[item.id];

                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "border rounded-lg p-3 transition-all hover:shadow-sm cursor-pointer",
                          colorClass
                        )}
                        onClick={() => onItemClick?.(item)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm truncate">{item.title}</h4>
                              {item.date && (
                                <p className="text-xs opacity-70 mt-0.5">
                                  {formatDateTime(item.date)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {item.status && (
                              <Badge variant="outline" className={cn("text-xs", statusClass)}>
                                {item.status}
                              </Badge>
                            )}
                            {item.description && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(item.id);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Expanded content */}
                        {isExpanded && item.description && (
                          <div className="mt-2 pt-2 border-t border-current/10">
                            <p className="text-sm opacity-80 whitespace-pre-wrap">
                              {item.description}
                            </p>
                            {item.amount && (
                              <p className="text-sm font-medium mt-2">
                                Amount: ${item.amount.toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default CaseTimeline;
